import { calculateCost } from '../../../../constants/ai';
import { buildCostSummary } from '../../../../lib/ai/costs';
import { toTrimmedNonEmptyString } from '../../../../lib/utils/type-guards';
import { formatCostDisplay, formatQuantity, formatTranscriptSource } from './formatters';
import type { SummaryDetailsCopyArguments } from './types';

export function buildSummaryDetailsCopy({
  resolvedModelLabel,
  resolvedSummaryLengthLabel,
  modelId,
  summaryTruncated,
  contentTruncated,
  sourceCharacters,
  contentBudgetCharacters,
  transcriptAvailable,
  transcriptSource,
  usage,
  cost,
  powerUserEnabled,
}: SummaryDetailsCopyArguments): string {
  const parts: string[] = [
    `Model · ${resolvedModelLabel ?? 'unknown'}`,
    `Target length · ${resolvedSummaryLengthLabel ?? '—'}`,
  ];

  if (typeof sourceCharacters === 'number') {
    const formatted = formatQuantity(sourceCharacters);
    let sourceKind: string;
    if (transcriptAvailable === true) {
      sourceKind = `Transcript${formatTranscriptSource(transcriptSource ?? null)}`;
    } else if (transcriptAvailable === false) {
      sourceKind = 'HTML';
    } else {
      sourceKind = 'Unknown source';
    }
    parts.push(`Source · ${formatted} chars (${sourceKind})`);
  }

  const renderCost = () => {
    const resolveEffectiveCost = () => {
      if (cost) {
        return cost;
      }

      const candidateModelId = toTrimmedNonEmptyString(modelId) ?? toTrimmedNonEmptyString(resolvedModelLabel) ?? null;

      if (!candidateModelId) {
        return null;
      }

      const promptTokens = Number.isFinite(usage?.promptTokens) ? (usage?.promptTokens ?? 0) : 0;
      const completionTokens = Number.isFinite(usage?.completionTokens) ? (usage?.completionTokens ?? 0) : 0;
      if (promptTokens === 0 && completionTokens === 0) {
        return null;
      }

      try {
        return buildCostSummary(candidateModelId, promptTokens, completionTokens);
      } catch {
        return null;
      }
    };

    const effectiveCost = resolveEffectiveCost();
    if (!effectiveCost) {
      return false;
    }

    const promptTokens = Number.isFinite(usage?.promptTokens) ? (usage?.promptTokens ?? 0) : 0;
    const completionTokens = Number.isFinite(usage?.completionTokens) ? (usage?.completionTokens ?? 0) : 0;
    const hasUsageTokens = promptTokens > 0 || completionTokens > 0;

    const pricingModelId = toTrimmedNonEmptyString(effectiveCost.pricingModel) ?? null;

    let estimatedCost: ReturnType<typeof calculateCost> | null = null;
    if (hasUsageTokens && pricingModelId) {
      try {
        estimatedCost = calculateCost(pricingModelId, promptTokens, completionTokens);
      } catch {
        estimatedCost = null;
      }
    }

    const totalCost = formatCostDisplay(effectiveCost.totalCostCents, estimatedCost?.totalCost ?? null);
    const inputCost = formatCostDisplay(effectiveCost.inputCostCents, estimatedCost?.inputCost ?? null);
    const outputCost = formatCostDisplay(effectiveCost.outputCostCents, estimatedCost?.outputCost ?? null);

    const breakdown: string[] = [];

    if (inputCost.hasValue) {
      breakdown.push(`${inputCost.display} input`);
    }
    if (outputCost.hasValue) {
      breakdown.push(`${outputCost.display} output`);
    }

    if (breakdown.length > 0) {
      parts.push(`Cost · ${totalCost.display} (${breakdown.join(' · ')})`);
    } else {
      parts.push(`Cost · ${totalCost.display}`);
    }

    return true;
  };

  const costRendered = powerUserEnabled ? renderCost() : false;

  if (!costRendered && typeof contentBudgetCharacters === 'number') {
    const formattedBudget = formatQuantity(contentBudgetCharacters);
    parts.push(`Budget · ${formattedBudget} chars (estimated prompt allowance)`);
  }

  if (usage) {
    const totalTokens = Number.isFinite(usage.totalTokens) ? usage.totalTokens : 0;
    const promptTokens = Number.isFinite(usage.promptTokens) ? usage.promptTokens : 0;
    const completionTokens = Number.isFinite(usage.completionTokens) ? usage.completionTokens : 0;
    const reasoningTokens =
      typeof usage.reasoningTokens === 'number' && Number.isFinite(usage.reasoningTokens) ? usage.reasoningTokens : 0;

    const hasTokenData = totalTokens > 0 || promptTokens > 0 || completionTokens > 0 || reasoningTokens > 0;
    if (hasTokenData) {
      const breakdown: string[] = [];
      if (promptTokens > 0) {
        breakdown.push(`${formatQuantity(promptTokens)} prompt`);
      }
      if (completionTokens > 0) {
        breakdown.push(`${formatQuantity(completionTokens)} completion`);
      }
      if (reasoningTokens > 0) {
        breakdown.push(`${formatQuantity(reasoningTokens)} reasoning`);
      }

      const totalLabel = `${formatQuantity(totalTokens)} total`;
      if (breakdown.length > 0) {
        parts.push(`Tokens · ${totalLabel} (${breakdown.join(' · ')})`);
      } else {
        parts.push(`Tokens · ${totalLabel}`);
      }
    }
  }

  if (summaryTruncated === true) {
    parts.push('Stopped when we hit the token hint.');
  }
  if (contentTruncated === true) {
    parts.push('Source content was already clipped upstream.');
  }

  return parts.join('\n');
}
